<script lang="ts">
import { RouteNames } from "@/utils/constants.ts";

export function getRouteOnboardBusinessQuestionnaire() {
  return {
    name: RouteNames.ONBOARDING_BUSINESS_QUESTIONNAIRE
  };
}
</script>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import CardLayout from "@/components/layout/CardLayout.vue";
import { TextInput } from "@/components/form/index.ts";
import InputSelect from "@/components/form/InputSelect.vue";
import BasicFormTitle from "@/components/basic/BasicFormTitle.vue";
import { getRouteOnboardBusinessProfile } from "@/views/Onboarding/OnboardingBusinessProfile.vue";
import { getRouteOnboardBusinessOwners } from "@/views/Onboarding/OnboardingBusinessOwners.vue";
const router = useRouter();
const isLoading = ref(false);

// Purpose of the Account
const purposeOfAccount = ref("");

// Number of Employees
const numberOfEmployees = ref("");

// Source of incoming funds
const sourceInvestmentByOwners = ref(false);
const sourceIncomeReceivables = ref(false);
const sourceSubsidies = ref(false);
const sourceBorrowings = ref(false);
const sourcePrepayments = ref(false);
const sourceOther = ref(false);
const sourceOtherText = ref("");

// Source of Wealth
const wealthInvestmentByOwners = ref(false);
const wealthBusinessActivities = ref(false);
const wealthLoans = ref(false);
const wealthOther = ref(false);
const wealthOtherText = ref("");

// Monthly Turnover
const monthlyTurnover = ref("");
const monthlyTurnoverOtherText = ref("");

const turnoverOptions = {
  "up_to_2000": "Up to 2,000 EUR",
  "2000_to_10000": "2,000 - 10,000 EUR",
  "10000_to_20000": "10,000 - 20,000 EUR",
  "20000_to_50000": "20,000 - 50,000 EUR",
  "50000_to_100000": "50,000 - 100,000 EUR",
  "100000_to_500000": "100,000 - 500,000 EUR",
  "500000_to_1000000": "500,000 - 1,000,000 EUR",
  "over_1000000": "Over 1,000,000 EUR",
  "other": "Other"
};

// Average Transaction Value
const averageTransactionValue = ref("");
const averageTransactionValueOtherText = ref("");

const transactionValueOptions = {
  "up_to_1000": "Up to 1,000 EUR",
  "1000_to_5000": "1,000 - 5,000 EUR",
  "5000_to_10000": "5,000 - 10,000 EUR",
  "10000_to_50000": "10,000 - 50,000 EUR",
  "50000_to_100000": "50,000 - 100,000 EUR",
  "over_100000": "Over 100,000 EUR",
  "other": "Other"
};

// Incoming Transactions per Month
const incomingTransactions = ref("");
const incomingTransactionsOtherText = ref("");

const incomingTransactionsOptions = {
  "up_to_10": "Up to 10",
  "10_to_50": "10 - 50",
  "50_to_100": "50 - 100",
  "100_to_500": "100 - 500",
  "over_500": "Over 500",
  "other": "Other"
};

// Outgoing Transactions per Month
const outgoingTransactions = ref("other");
const outgoingTransactionsOtherText = ref("");

const outgoingTransactionsOptions = {
  "up_to_10": "Up to 10",
  "10_to_50": "10 - 50",
  "50_to_100": "50 - 100",
  "100_to_500": "100 - 500",
  "over_500": "Over 500",
  "other": "Other"
};

// Major Counterparties for Incoming Transactions (Customers)
interface ICounterparty {
  id: string;
  counterparty: string;
  lineOfBusiness: string;
}

const incomingCounterparties = ref<ICounterparty[]>([
  {
    id: '1',
    counterparty: '',
    lineOfBusiness: ''
  }
]);

function addIncomingCounterparty() {
  incomingCounterparties.value.push({
    id: Date.now().toString(),
    counterparty: '',
    lineOfBusiness: ''
  });
}

// Major Counterparties for Outgoing Transactions (Suppliers)
const outgoingCounterparties = ref<ICounterparty[]>([
  {
    id: '1',
    counterparty: '',
    lineOfBusiness: ''
  }
]);

function addOutgoingCounterparty() {
  outgoingCounterparties.value.push({
    id: Date.now().toString(),
    counterparty: '',
    lineOfBusiness: ''
  });
}

// Countries for Fund Transfers
const countriesEuEea = ref(false);
const countriesOther = ref(false);
const countriesOtherText = ref("");

const isFormValid = computed(() => {
  const hasSourceOfFunds = sourceInvestmentByOwners.value ||
                           sourceIncomeReceivables.value ||
                           sourceSubsidies.value ||
                           sourceBorrowings.value ||
                           sourcePrepayments.value ||
                           sourceOther.value;

  const hasWealthSource = wealthInvestmentByOwners.value ||
                          wealthBusinessActivities.value ||
                          wealthLoans.value ||
                          wealthOther.value;

  const hasCountries = countriesEuEea.value || countriesOther.value;

  return !!(purposeOfAccount.value &&
         numberOfEmployees.value &&
         hasSourceOfFunds &&
         hasWealthSource &&
         monthlyTurnover.value &&
         averageTransactionValue.value &&
         incomingTransactions.value &&
         outgoingTransactions.value &&
         hasCountries);
});

async function submitForm() {
  isLoading.value = true;
  try {
    const questionnaireData = {
      purposeOfAccount: purposeOfAccount.value,
      numberOfEmployees: numberOfEmployees.value,
      sourceOfIncomingFunds: {
        investmentByOwners: sourceInvestmentByOwners.value,
        incomeReceivables: sourceIncomeReceivables.value,
        subsidies: sourceSubsidies.value,
        borrowings: sourceBorrowings.value,
        prepayments: sourcePrepayments.value,
        other: sourceOther.value,
        otherText: sourceOtherText.value
      },
      sourceOfWealth: {
        investmentByOwners: wealthInvestmentByOwners.value,
        businessActivities: wealthBusinessActivities.value,
        loans: wealthLoans.value,
        other: wealthOther.value,
        otherText: wealthOtherText.value
      },
      monthlyTurnover: monthlyTurnover.value,
      monthlyTurnoverOther: monthlyTurnoverOtherText.value,
      averageTransactionValue: averageTransactionValue.value,
      averageTransactionValueOther: averageTransactionValueOtherText.value,
      incomingTransactions: incomingTransactions.value,
      incomingTransactionsOther: incomingTransactionsOtherText.value,
      outgoingTransactions: outgoingTransactions.value,
      outgoingTransactionsOther: outgoingTransactionsOtherText.value,
      incomingCounterparties: incomingCounterparties.value,
      outgoingCounterparties: outgoingCounterparties.value,
      countries: {
        euEea: countriesEuEea.value,
        other: countriesOther.value,
        otherText: countriesOtherText.value
      }
    };

    // Save questionnaire data
    console.log("Business questionnaire data:", questionnaireData);
    // Note: Backend API endpoint not yet available, using mock data for now
    // await personService.createBusinessQuestionnaire("business-questionnaire", questionnaireData);

    // Refresh person state to get updated data
    // await personStore.getState();

    // Navigate to business owners page
    await router.push(getRouteOnboardBusinessOwners());
  } catch (e) {
    console.error("Error submitting business questionnaire:", e);
  } finally {
    isLoading.value = false;
  }
}

const goBack = () => {
  router.push(getRouteOnboardBusinessProfile());
};
</script>

<template>
  <CardLayout
    title="Business Account"
    subtitle="Application for opening and maintaining a business current account"
    heading="Questionnaire"
    :max-steps="4"
    :current-step="3"
    :next="submitForm"
    :back="goBack"
    :isAllowedNext="isFormValid"
  >
    <div class="flex flex-col w-full gap-6 md:gap-8">
      <!-- Purpose of the Account -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Purpose of the Account
        </BasicFormTitle>
      </div>

      <div class="px-4 md:px-0">
        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Describe the purpose of opening this account *</label>
          <textarea
            v-model="purposeOfAccount"
            placeholder="Enter the purpose of the account"
            rows="4"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          ></textarea>
        </div>
      </div>

      <!-- Number of Employees -->
      <div class="px-4 md:px-0">
        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Number of Employees *</label>
          <TextInput
            type="text"
            v-model="numberOfEmployees"
            placeholder="Enter number of employees"
          />
        </div>
      </div>

      <!-- Source of incoming funds -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          What will be the source of incoming funds to your account?
        </BasicFormTitle>
      </div>

      <div class="px-4 md:px-0">
        <div class="flex flex-col gap-3">
          <label class="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              v-model="sourceInvestmentByOwners"
              class="w-4 h-4 mt-0.5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="text-sm text-gray-700">Investment by company owners</span>
          </label>

          <label class="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              v-model="sourceIncomeReceivables"
              class="w-4 h-4 mt-0.5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="text-sm text-gray-700">Income or interest receivables</span>
          </label>

          <label class="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              v-model="sourceSubsidies"
              class="w-4 h-4 mt-0.5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="text-sm text-gray-700">Subsidies</span>
          </label>

          <label class="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              v-model="sourceBorrowings"
              class="w-4 h-4 mt-0.5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="text-sm text-gray-700">Borrowings</span>
          </label>

          <label class="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              v-model="sourcePrepayments"
              class="w-4 h-4 mt-0.5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="text-sm text-gray-700">Prepayments</span>
          </label>

          <div class="flex flex-col gap-2">
            <label class="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                v-model="sourceOther"
                class="w-4 h-4 mt-0.5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
              />
              <span class="text-sm text-gray-700">Other</span>
            </label>
            <TextInput
              type="text"
              v-model="sourceOtherText"
              placeholder="Please specify"
            />
          </div>
        </div>
      </div>

      <!-- Source of Wealth -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Source of Wealth
        </BasicFormTitle>
      </div>

      <div class="px-4 md:px-0">
        <div class="flex flex-col gap-3">
          <label class="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              v-model="wealthInvestmentByOwners"
              class="w-4 h-4 mt-0.5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="text-sm text-gray-700">Investment by company owners</span>
          </label>

          <label class="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              v-model="wealthBusinessActivities"
              class="w-4 h-4 mt-0.5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="text-sm text-gray-700">Business activities</span>
          </label>

          <label class="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              v-model="wealthLoans"
              class="w-4 h-4 mt-0.5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="text-sm text-gray-700">Loans</span>
          </label>

          <div class="flex flex-col gap-2">
            <label class="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                v-model="wealthOther"
                class="w-4 h-4 mt-0.5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
              />
              <span class="text-sm text-gray-700">Other</span>
            </label>
            <TextInput
              type="text"
              v-model="wealthOtherText"
              placeholder="Please specify"
            />
          </div>
        </div>
      </div>

      <!-- Monthly Turnover -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Please specify your planned monthly turnover
        </BasicFormTitle>
      </div>

      <div class="px-4 md:px-0">
        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Monthly Turnover *</label>
          <InputSelect
            :list="turnoverOptions"
            v-model="monthlyTurnover"
          >
            <template #selected>
              <span v-if="monthlyTurnover && turnoverOptions[monthlyTurnover]">
                {{ turnoverOptions[monthlyTurnover] }}
              </span>
              <span v-else class="text-gray-500">Select monthly turnover</span>
            </template>
            <template #item="{ item }">
              <span>{{ item }}</span>
            </template>
          </InputSelect>
        </div>
      </div>

      <!-- Other text input for Monthly Turnover -->
      <div class="px-4 md:px-0">
        <label class="text-sm text-gray-500">Other</label>
        <TextInput
          type="text"
          v-model="monthlyTurnoverOtherText"
          placeholder="Other"
        />
      </div>

      <!-- Average Transaction Value -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Please specify the planned value of average one-time transaction
        </BasicFormTitle>
      </div>

      <div class="px-4 md:px-0">
        <div class="flex flex-col gap-y-2">
          <InputSelect
            :list="transactionValueOptions"
            v-model="averageTransactionValue"
          >
            <template #selected>
              <span v-if="averageTransactionValue && transactionValueOptions[averageTransactionValue]">
                {{ transactionValueOptions[averageTransactionValue] }}
              </span>
              <span v-else class="text-gray-500">Select average transaction value</span>
            </template>
            <template #item="{ item }">
              <span>{{ item }}</span>
            </template>
          </InputSelect>
        </div>
      </div>

      <!-- Other text input for Average Transaction Value -->
      <div class="px-4 md:px-0">
        <label class="text-sm text-gray-500">Other</label>
        <TextInput
          type="text"
          v-model="averageTransactionValueOtherText"
          placeholder="Other"
        />
      </div>

      <!-- Incoming Transactions per Month -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Please specify the planned amount of incoming transactions per month
        </BasicFormTitle>
      </div>

      <div class="px-4 md:px-0">
        <div class="flex flex-col gap-y-2">
          <InputSelect
            :list="incomingTransactionsOptions"
            v-model="incomingTransactions"
          >
            <template #selected>
              <span v-if="incomingTransactions && incomingTransactionsOptions[incomingTransactions]">
                {{ incomingTransactionsOptions[incomingTransactions] }}
              </span>
              <span v-else class="text-gray-500">Select incoming transactions</span>
            </template>
            <template #item="{ item }">
              <span>{{ item }}</span>
            </template>
          </InputSelect>
        </div>
      </div>

      <!-- Other text input for Incoming Transactions -->
      <div class="px-4 md:px-0">
        <label class="text-sm text-gray-500">Other</label>
        <TextInput
          type="text"
          v-model="incomingTransactionsOtherText"
          placeholder="Other"
        />
      </div>

      <!-- Outgoing Transactions per Month -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Please specify the planned amount of outgoing transactions per month
        </BasicFormTitle>
      </div>

      <div class="px-4 md:px-0">
        <div class="flex flex-col gap-y-2">
          <InputSelect
            :list="outgoingTransactionsOptions"
            v-model="outgoingTransactions"
          >
            <template #selected>
              <span v-if="outgoingTransactions && outgoingTransactionsOptions[outgoingTransactions]">
                {{ outgoingTransactionsOptions[outgoingTransactions] }}
              </span>
              <span v-else class="text-gray-500">Select outgoing transactions</span>
            </template>
            <template #item="{ item }">
              <span>{{ item }}</span>
            </template>
          </InputSelect>
        </div>
      </div>

      <!-- Other text input for Outgoing Transactions -->
      <div class="px-4 md:px-0">
        <label class="text-sm text-gray-500">Other</label>
        <TextInput
          type="text"
          v-model="outgoingTransactionsOtherText"
          placeholder="Other"
        />
      </div>

      <!-- Major Counterparties for Incoming Transactions (Customers) -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Please specify your 3 major counterparties for incoming transactions (customers) and their line of business
        </BasicFormTitle>
      </div>

      <div v-for="(counterparty) in incomingCounterparties" :key="counterparty.id" class="px-4 md:px-0">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
          <div class="flex flex-col gap-y-2">
            <label class="text-sm text-gray-700">Counterparty</label>
            <TextInput
              type="text"
              v-model="counterparty.counterparty"
              placeholder="Enter Counterparty"
            />
          </div>
          <div class="flex flex-col gap-y-2">
            <label class="text-sm text-gray-700">Line of Business</label>
            <TextInput
              type="text"
              v-model="counterparty.lineOfBusiness"
              placeholder="Enter Line of Business"
            />
          </div>
        </div>
      </div>

      <div class="px-4 md:px-0">
        <button
          @click="addIncomingCounterparty"
          class="w-full py-3 border-2 border-dashed border-blue-500 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors font-medium flex items-center justify-center gap-2"
        >
          <span class="text-xl">+</span>
          Add
        </button>
      </div>

      <!-- Countries for Fund Transfers -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Please specify all countries to which you expect to send funds
        </BasicFormTitle>
      </div>

      <div class="px-4 md:px-0">
        <div class="flex flex-col gap-3">
          <label class="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              v-model="countriesEuEea"
              class="w-4 h-4 mt-0.5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="text-sm text-gray-700">EU/ EEA counterparties</span>
          </label>

          <label class="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              v-model="countriesOther"
              class="w-4 h-4 mt-0.5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="text-sm text-gray-700">If other than EU/ EEA counterparties, please list the countries below:</span>
          </label>

          <TextInput
            type="text"
            v-model="countriesOtherText"
            placeholder="If other than EU/ EEA counterparties, please list the countries below:"
          />
        </div>
      </div>

      <!-- Major Counterparties for Outgoing Transactions (Suppliers) -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Please specify your 3 major counterparties for outgoing transactions (suppliers) and their line of business
        </BasicFormTitle>
      </div>

      <div v-for="(counterparty) in outgoingCounterparties" :key="counterparty.id" class="px-4 md:px-0">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
          <div class="flex flex-col gap-y-2">
            <label class="text-sm text-gray-700">Counterparty</label>
            <TextInput
              type="text"
              v-model="counterparty.counterparty"
              placeholder="Enter Counterparty"
            />
          </div>
          <div class="flex flex-col gap-y-2">
            <label class="text-sm text-gray-700">Line of Business</label>
            <TextInput
              type="text"
              v-model="counterparty.lineOfBusiness"
              placeholder="Enter Line of Business"
            />
          </div>
        </div>
      </div>

      <div class="px-4 md:px-0">
        <button
          @click="addOutgoingCounterparty"
          class="w-full py-3 border-2 border-dashed border-blue-500 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors font-medium flex items-center justify-center gap-2"
        >
          <span class="text-xl">+</span>
          Add
        </button>
      </div>

      <div v-if="isLoading" class="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400 px-4 md:px-0">
        <div class="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent"></div>
        <span class="text-sm">Submitting...</span>
      </div>
    </div>
  </CardLayout>
</template>

<style scoped lang="scss">
</style>
